import React, { useState, useEffect } from "react";
import { MainLayout } from "../layout/MainLayout";
import { Loader, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PrimaryButton from "../ui/PrimaryButton";
import FileUploadZone from "./FileUploadZone";
import UploadProgressDisplay from "../common/UploadProgressDisplay";
import FileTable from "./FileTable";
import ErrorBanner from "../common/ErrorBanner";
import Card from "../common/Card";
import SectionHeader from "../common/SectionHeader";
import FileUploadService from "../services/FileUploadService";

const FileManager = () => {
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);

  const projectID = localStorage.getItem("projectID");

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const data = await FileUploadService.fetchFiles(projectID);
      setFiles(data);
      setError(null);
    } catch (error) {
      setError("Failed to load files. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilesSelect = (files) => {
    setUploadFiles(files);
    handleFilesUpload(files);
  };

  const handleEpcFilesSelect = (files) => {
    setUploadFiles(files);
    handleEpcFilesUpload(files);
  };

  // Upload multiple files
  const handleFilesUpload = async (files) => {
    for (const file of files) {
      await FileUploadService.uploadFile(file, projectID, setUploadProgress);
    }

    setTimeout(() => {
      setUploadFiles([]);
      setUploadProgress({});
      fetchFiles();
    }, 2000);
  };

  const handleEpcFilesUpload = async (files) => {
    for (const file of files) {
      await FileUploadService.uploadFile(
        file,
        projectID,
        setUploadProgress,
        true
      );
    }

    setTimeout(() => {
      setUploadFiles([]);
      setUploadProgress({});
      toast.success("EPC certificate uploaded successfully");
    }, 2000);
  };

  const handleSelectFile = (fileId) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleDeleteFiles = async () => {
    if (
      !selectedFiles.length ||
      !window.confirm("Are you sure you want to delete the selected files?")
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      await FileUploadService.deleteFiles(selectedFiles);
      await fetchFiles();
      setSelectedFiles([]);
    } catch (error) {
      setError("Failed to delete files. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const renderDeleteButton = () => {
    if (selectedFiles.length === 0) return null;

    return (
      <PrimaryButton
        onClick={handleDeleteFiles}
        isLoading={isDeleting}
        loadingText="Deleting..."
        width="auto"
        className="bg-red-600 hover:bg-red-700"
      >
        <div className="flex items-center gap-2">
          <Trash2 className="w-4 h-4" />
          Delete Selected ({selectedFiles.length})
        </div>
      </PrimaryButton>
    );
  };

  const renderLoading = () => {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="w-8 h-8 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-600">Loading files...</p>
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card
            title="File Management"
            subtitle="Manage and organize your project files"
          >
            <ErrorBanner message={error} />

            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Upload Files
              </h2>

              <FileUploadZone
                onFilesSelect={handleFilesSelect}
                disabled={Object.keys(uploadProgress).length > 0}
                accept=".ifc,.rvt,.pdf,.xlsx,.zip"
                acceptedTypes={["IFC", "RVT", "PDF", "XLSX", "ZIP"]}
              />

              <UploadProgressDisplay progress={uploadProgress} />
            </div>

            <div>
              <SectionHeader
                title="Project Files"
                action={renderDeleteButton()}
              />

              {loading ? (
                renderLoading()
              ) : (
                <FileTable
                  files={files}
                  selectedFiles={selectedFiles}
                  onSelectFile={handleSelectFile}
                  onSelectAll={setSelectedFiles}
                />
              )}
            </div>

            {/* EPC Certificate Section */}
            <div className="mt-8">
              {/* EPC Upload Section */}
              <div className="bg-gray-50 rounded-lg p-6 mt-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Upload EPC Certificate
                </h2>

                <FileUploadZone
                  onFilesSelect={handleEpcFilesSelect}
                  disabled={Object.keys(uploadProgress).length > 0}
                  accept=".pdf"
                  acceptedTypes={["PDF"]}
                />

                <div className="mt-2 text-sm text-gray-500">
                  Please upload Energy Performance Certificate (EPC) in PDF
                  format
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default FileManager;
